import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import { getRole, updateRole, deleteRole, addCapabilityToRole, removeCapabilityFromRole } from '../../services/rbacService.js';
import NotAuthorized from '../../components/admin/NotAuthorized.jsx';
import RoleEditModal from '../../components/admin/RoleEditModal.jsx';
import AddCapabilityModal from '../../components/admin/AddCapabilityModal.jsx';
import DestructiveActionConfirm from '../../components/admin/DestructiveActionConfirm.jsx';
import ResultBanner from '../../components/admin/ResultBanner.jsx';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminCard from '../../components/admin/ui/AdminCard';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminStatusBadge from '../../components/admin/ui/AdminStatusBadge';
import AdminAlert from '../../components/admin/ui/AdminAlert';

export default function RoleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddCapabilityModal, setShowAddCapabilityModal] = useState(false);
  const [showRemoveCapabilityModal, setShowRemoveCapabilityModal] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [resultBanner, setResultBanner] = useState(null);
  const { has } = useCapabilities();

  const canReadRbac = has('admin:rbac:read');
  const canUpdateRole = has('admin:roles:update');
  const canDeleteRole = has('admin:roles:delete');
  const canManageCapabilities = has('admin:roles:manage-capabilities');

  useEffect(() => {
    if (!canReadRbac) {
      setLoading(false);
      return;
    }
    loadRole();
  }, [id, canReadRbac]);

  const loadRole = async () => {
    try {
      setLoading(true);
      setError(null);
      const roleData = await getRole(id);
      setRole(roleData);
    } catch (err) {
      setError(err.message || 'Failed to load role');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = async (data) => {
    try {
      setModalLoading(true);
      setModalError(null);
      const updatedRole = await updateRole(id, {
        name: data.name,
        description: data.description,
      }, data.reason);
      setRole(updatedRole);
      setShowEditModal(false);
      setResultBanner({
        type: 'success',
        message: 'Role updated successfully',
      });
    } catch (err) {
      setModalError(err.message || 'Failed to update role');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteRole = async (reason) => {
    try {
      setModalLoading(true);
      setModalError(null);
      await deleteRole(id, reason);
      setShowDeleteModal(false);
      setResultBanner({
        type: 'success',
        message: 'Role deleted successfully',
      });
      // Navigate back to roles list after a short delay
      setTimeout(() => {
        navigate(returnTo || '/admin/roles');
      }, 1000);
    } catch (err) {
      setModalError(err.message || 'Failed to delete role');
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddCapability = async (capability, reason) => {
    try {
      setModalLoading(true);
      setModalError(null);
      const updatedRole = await addCapabilityToRole(id, capability, reason);
      setRole(updatedRole);
      setShowAddCapabilityModal(false);
      setResultBanner({
        type: 'success',
        message: `Capability "${capability}" added successfully`,
      });
    } catch (err) {
      setModalError(err.message || 'Failed to add capability');
    } finally {
      setModalLoading(false);
    }
  };

  const handleRemoveCapability = async (reason) => {
    try {
      setModalLoading(true);
      setModalError(null);
      const updatedRole = await removeCapabilityFromRole(id, selectedCapability, reason);
      setRole(updatedRole);
      setShowRemoveCapabilityModal(false);
      setSelectedCapability(null);
      setResultBanner({
        type: 'success',
        message: `Capability "${selectedCapability}" removed successfully`,
      });
    } catch (err) {
      setModalError(err.message || 'Failed to remove capability');
    } finally {
      setModalLoading(false);
    }
  };

  if (!canReadRbac) {
    return <NotAuthorized requiredCapability="admin:rbac:read" />;
  }

  if (loading) {
    return (
      <div className="admin-surface">
        <div className="admin-loading">Loading role...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-surface">
        <AdminAlert 
          type="error" 
          message={
            <>
              Error: {error}
              <AdminButton variant="secondary" onClick={loadRole} style={{ marginLeft: 'var(--spacing-3)' }}>
                Retry
              </AdminButton>
            </>
          }
        />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="admin-surface">
        <AdminAlert 
          type="error" 
          message={
            <>
              Role not found
              <AdminButton variant="secondary" onClick={() => navigate(returnTo || '/admin/roles')} style={{ marginLeft: 'var(--spacing-3)' }}>
                {returnTo ? 'Back' : 'Back to Roles'}
              </AdminButton>
            </>
          }
        />
      </div>
    );
  }

  const isSystemRole = role.systemRole === true;

  return (
    <div className="admin-surface">
      <AdminPageHeader 
        title="Role Details"
        actions={
          <AdminButton variant="secondary" onClick={() => navigate(returnTo || '/admin/roles')}>
            ← {returnTo ? 'Back' : 'Back to Roles'}
          </AdminButton>
        }
      />

      {resultBanner && (
        <ResultBanner
          type={resultBanner.type}
          message={resultBanner.message}
          onClose={() => setResultBanner(null)}
        />
      )}

      <AdminCard
        title="Role Information"
        actions={canUpdateRole ? (
          <AdminButton variant="secondary" onClick={() => { setShowEditModal(true); setModalError(null); }}>
            Edit
          </AdminButton>
        ) : null}
      >
        <div className="admin-grid admin-grid-cols-2" style={{ gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
          <div>
            <strong className="admin-text-primary">Name:</strong> <code className="admin-code">{role.name}</code>
            {isSystemRole && (
              <AdminStatusBadge variant="info" style={{ marginLeft: 'var(--spacing-2)' }}>System Role</AdminStatusBadge>
            )}
          </div>
          <div>
            <strong className="admin-text-primary">Display Name:</strong> <span className="admin-text-secondary">{role.displayName}</span>
          </div>
          <div>
            <strong className="admin-text-primary">User Count:</strong> <span className="admin-text-secondary">{role.userCount || 0}</span>
          </div>
          <div>
            <strong className="admin-text-primary">Role ID:</strong> <code className="admin-code">{role.id}</code>
          </div>
        </div>
        {role.description && (
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <strong className="admin-text-primary">Description:</strong> <span className="admin-text-secondary">{role.description}</span>
          </div>
        )}
        {canDeleteRole && !isSystemRole && (
          <div style={{ marginTop: 'var(--spacing-4)', paddingTop: 'var(--spacing-4)', borderTop: '1px solid var(--color-border-light)' }}>
            <AdminButton
              variant="error"
              onClick={() => { setShowDeleteModal(true); setModalError(null); }}
              ariaLabel="Delete role"
            >
              Delete Role
            </AdminButton>
          </div>
        )}
      </AdminCard>

      <AdminCard
        title="Capabilities"
        actions={canManageCapabilities ? (
          <AdminButton variant="primary" onClick={() => { setShowAddCapabilityModal(true); setModalError(null); }} data-testid="add-capability-button">
            Add Capability
          </AdminButton>
        ) : null}
      >
        {role.capabilities && role.capabilities.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            {role.capabilities.map((capability, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-2)', backgroundColor: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                <code className="admin-code">{capability}</code>
                {canManageCapabilities && (
                  <AdminButton
                    variant="error"
                    size="small"
                    onClick={() => {
                      setSelectedCapability(capability);
                      setShowRemoveCapabilityModal(true);
                      setModalError(null);
                    }}
                    ariaLabel={`Remove capability ${capability}`}
                  >
                    Remove
                  </AdminButton>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-text-tertiary">No capabilities assigned</p>
        )}
      </AdminCard>

      <RoleEditModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setModalError(null);
        }}
        onConfirm={handleEditRole}
        role={role}
        loading={modalLoading}
        error={modalError}
      />

      <DestructiveActionConfirm
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setModalError(null);
        }}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        description={`Delete role "${role.displayName}" (${role.name})? This action cannot be undone.`}
        confirmLabel="Delete Role"
        reasonLabel="Reason for deletion"
        reasonPlaceholder="Enter a reason for deleting this role..."
        loading={modalLoading}
        error={modalError}
      />

      <AddCapabilityModal
        open={showAddCapabilityModal}
        onClose={() => {
          setShowAddCapabilityModal(false);
          setModalError(null);
        }}
        onConfirm={handleAddCapability}
        existingCapabilities={role.capabilities || []}
        loading={modalLoading}
        error={modalError}
      />

      <DestructiveActionConfirm
        open={showRemoveCapabilityModal}
        onClose={() => {
          setShowRemoveCapabilityModal(false);
          setSelectedCapability(null);
          setModalError(null);
        }}
        onConfirm={handleRemoveCapability}
        title="Remove Capability"
        description={selectedCapability ? `Remove capability "${selectedCapability}" from role "${role.displayName}"?` : 'Remove capability from role?'}
        confirmLabel="Remove Capability"
        reasonLabel="Reason for removal"
        reasonPlaceholder="Enter a reason for removing this capability..."
        loading={modalLoading}
        error={modalError}
      />
    </div>
  );
}


